import { useState, useEffect } from 'react';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';

  const scrollToSection = (sectionId: string) => {
    setMobileMenuOpen(false);
    if (isHome) {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  useEffect(() => {
    if (isHome && location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [isHome, location.hash]);

  const navLinks = [
    { name: 'Shop', action: () => navigate('/shop') },
    { name: 'Steering Wheels', action: () => { setMobileMenuOpen(false); navigate('/steering-wheel'); } },
    { name: 'About', action: () => scrollToSection('about') },
    { name: 'Gallery', action: () => scrollToSection('gallery') },
    { name: 'Reviews', action: () => scrollToSection('reviews') },
    { name: 'Contact', action: () => scrollToSection('contact') },
  ];

  return (
    <>
      {/* Top Bar - hidden on mobile */}
      <div className="hidden md:block bg-black border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-10">
            <div className="flex items-center gap-4 md:gap-8 text-xs text-gray-400">
              <a href="tel:+381642428074" className="flex items-center gap-2 hover:text-white transition-colors">
                <i className="ri-phone-line"></i>
                <span>+381 64 242 8074</span>
              </a>
              <a href="mailto:info@budzevina.com" className="hidden md:flex items-center gap-2 hover:text-white transition-colors">
                <i className="ri-mail-line"></i>
                <span>info@budzevina.com</span>
              </a>
            </div>
            <div className="flex items-center gap-4 md:gap-6 text-xs text-gray-400">
              <div className="hidden md:flex items-center gap-2">
                <i className="ri-shield-check-line text-white"></i>
                <span className="text-white">5-Year Warranty</span>
              </div>
              <div className="hidden md:flex items-center gap-2">
                <i className="ri-global-line"></i>
                <span>Worldwide Shipping</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`fixed top-0 md:top-10 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'bg-white border-b border-gray-200 shadow-md'
          : 'bg-transparent'
      }`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo — white on hero, black when scrolled */}
            <button onClick={() => navigate('/')} className="flex items-center cursor-pointer group">
              <img
                src={(scrolled || mobileMenuOpen) ? '/images/budzevina-logo-crni.png' : '/images/budzevina-logo-beli.png'}
                alt="Budzevina Carbon"
                className="h-9 md:h-11 w-auto object-contain transition-all duration-300"
              />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={link.action}
                  className={`group relative text-sm font-medium tracking-wide transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-gray-700 hover:text-black'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 w-0 h-[1px] group-hover:w-full transition-all duration-300 ${
                    scrolled ? 'bg-black' : 'bg-white'
                  }`}></span>
                </button>
              ))}
            </div>

            {/* CTA Button - desktop */}
            <div className="hidden lg:block">
              <button
                onClick={() => navigate('/order')}
                className={`px-7 py-3 text-sm font-semibold tracking-wide transition-all whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'bg-black text-white hover:bg-gray-800'
                    : 'bg-white text-black hover:bg-gray-200'
                }`}
              >
                GET QUOTE
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
                scrolled ? 'text-black' : 'text-white'
              }`}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t bg-white border-gray-200">
            <div className="max-w-[1400px] mx-auto px-4 py-6">
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <button
                    key={link.name}
                    onClick={link.action}
                    className="text-base font-medium transition-colors py-3 border-b cursor-pointer text-left text-gray-800 hover:text-black border-gray-100"
                  >
                    {link.name}
                  </button>
                ))}
                <button
                  onClick={() => { setMobileMenuOpen(false); navigate('/order'); }}
                  className="px-7 py-3 text-sm font-semibold tracking-wide transition-all mt-4 whitespace-nowrap cursor-pointer w-full bg-black text-white hover:bg-gray-800"
                >
                  GET QUOTE
                </button>
                {/* Mobile contact info */}
                <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-gray-200">
                  <a href="tel:+381642428074" className="flex items-center gap-2 text-xs text-gray-500 hover:text-black transition-colors">
                    <i className="ri-phone-line"></i>
                    <span>+381 64 242 8074</span>
                  </a>
                  <a href="mailto:info@budzevina.com" className="flex items-center gap-2 text-xs text-gray-500 hover:text-black transition-colors">
                    <i className="ri-mail-line"></i>
                    <span>info@budzevina.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
